import React from 'react';
import { CheckCircle, Circle, ArrowRight } from 'lucide-react';

const roadmapSteps = [
  {
    week: "Week 1-2",
    title: "Foundation",
    modules: ["OSHA Basics", "Hazard Recognition"],
    description: "Build your safety knowledge foundation with core OSHA concepts and hazard identification skills.",
    color: "emerald"
  },
  {
    week: "Week 3-4",
    title: "Protection",
    modules: ["PPE Training", "Fire Safety"],
    description: "Master personal protective equipment selection and emergency preparedness procedures.",
    color: "blue"
  },
  {
    week: "Week 5-6",
    title: "Specialized Skills",
    modules: ["Electrical Safety", "Ergonomics"],
    description: "Develop expertise in electrical hazards, lockout/tagout, and workplace ergonomics.",
    color: "purple"
  },
  {
    week: "Week 7-8",
    title: "Advanced & Certification",
    modules: ["Specialized Topics", "Compliance & Cert"],
    description: "Complete advanced safety topics and earn your certificate of completion.",
    color: "amber"
  }
];

const CourseRoadmap = () => {
  return (
    <section className="py-20 bg-gradient-to-b from-white to-slate-50">
      <div className="container mx-auto px-6">
        {/* Section header */}
        <div className="text-center mb-12">
          <span className="text-emerald-600 font-semibold text-sm uppercase tracking-wider">Your Learning Path</span>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mt-2 mb-4">
            8-Week Course Roadmap
          </h2>
          <p className="text-slate-600 max-w-2xl mx-auto">
            Follow our structured learning path to become a workplace safety expert
          </p>
        </div>

        {/* Timeline */}
        <div className="max-w-4xl mx-auto">
          <div className="relative">
            {/* Connecting line */}
            <div className="absolute left-8 md:left-1/2 top-0 bottom-0 w-0.5 bg-slate-200 transform md:-translate-x-1/2" />

            {roadmapSteps.map((step, index) => (
              <div 
                key={index} 
                className={`relative flex items-start gap-6 mb-12 last:mb-0 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                {/* Timeline dot */}
                <div className="absolute left-8 md:left-1/2 transform -translate-x-1/2 z-10">
                  <div className={`w-4 h-4 rounded-full bg-${step.color}-500 ring-4 ring-white shadow-lg`} />
                </div>

                {/* Content card */}
                <div className={`ml-20 md:ml-0 md:w-[calc(50%-2rem)] ${
                  index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'
                }`}>
                  <div className="bg-white rounded-xl p-6 shadow-lg border border-slate-100 hover:shadow-xl transition-shadow">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold mb-3 ${
                      step.color === 'emerald' ? 'bg-emerald-100 text-emerald-700' :
                      step.color === 'blue' ? 'bg-blue-100 text-blue-700' :
                      step.color === 'purple' ? 'bg-purple-100 text-purple-700' :
                      'bg-amber-100 text-amber-700'
                    }`}>
                      {step.week}
                    </span>
                    <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                    <p className="text-slate-600 text-sm mb-3">{step.description}</p>
                    <div className={`flex flex-wrap gap-2 ${index % 2 === 0 ? 'md:justify-end' : ''}`}>
                      {step.modules.map((module, i) => (
                        <span 
                          key={i}
                          className="flex items-center gap-1 text-xs text-slate-500 bg-slate-50 px-2 py-1 rounded"
                        >
                          <CheckCircle className="w-3 h-3 text-emerald-500" />
                          {module}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Spacer for alternating layout */}
                <div className="hidden md:block md:w-[calc(50%-2rem)]" />
              </div>
            ))}
          </div>

          {/* Completion badge */}
          <div className="text-center mt-8">
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-emerald-500 text-white rounded-full font-semibold shadow-lg">
              <CheckCircle className="w-5 h-5" />
              Certificate of Completion
              <ArrowRight className="w-5 h-5" />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CourseRoadmap;
